import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Spin, message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import * as actions from 'store/actions/AuthActions';
import Stepper from './components/Stepper';
import StepGeneral from './components/StepGeneral';
import StepAddSigners from './components/StepAddSigners';
import StepAddFields from './components/StepAddFields';
import StepDistribution from './components/StepDistribution';
import DMSPDFViewer from './components/DMSPDFViewer';

const { Content } = Layout;

// Recipient colors for visual distinction
export const RECIPIENT_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DDA0DD', // Plum
  '#98D8C8', // Mint
  '#F7DC6F', // Gold
];

// Field types configuration
export const FIELD_TYPES = [
  { type: 'SIGNATURE', label: 'Signature', defaultWidth: 150, defaultHeight: 50 },
  { type: 'TEXT', label: 'Text', defaultWidth: 140, defaultHeight: 40 },
  { type: 'EMAIL', label: 'Email', defaultWidth: 180, defaultHeight: 40 },
  { type: 'NAME', label: 'Name', defaultWidth: 150, defaultHeight: 40 },
  { type: 'DATE', label: 'Date', defaultWidth: 120, defaultHeight: 40 },
  { type: 'INITIALS', label: 'Initials', defaultWidth: 80, defaultHeight: 40 },
];

// Helper to flatten fields from fieldsByRecipient
const getAllFields = (fieldsByRecipient) =>
  Object.values(fieldsByRecipient)
    .flat()
    .map((field) => ({ ...field }));

const DocumentWorkflow = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [fieldsByRecipient, setFieldsByRecipient] = useState({});
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeRecipient, setActiveRecipient] = useState(null);
  const [, setScaleFactor] = useState(1);

  // Fetch document details
  const fetchDocumentDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await actions.loadDocument(id);

      if (response) {
        setDocumentData({
          type: 'URL',
          data: response.signed_document_file || response.document_file
        });
        setFormData(response);

        // Load existing fields if present
        if (response.fields && Array.isArray(response.fields)) {
          const normalized = response.fields.map((field) => ({
            ...field,
            recipient_id: field.recipient_id || field.recipientId,
          }));
          const grouped = normalized.reduce((acc, field) => {
            const rid = field.recipient_id;
            if (rid) {
              if (!acc[rid]) acc[rid] = [];
              acc[rid].push(field);
            }
            return acc;
          }, {});
          setFieldsByRecipient(grouped);
        }

        // Set initial recipients with colors
        if (response.recipients && Array.isArray(response.recipients)) {
          setFormData(prev => ({
            ...prev,
            recipients: response.recipients.map((r, idx) => ({
              ...r,
              color: r.color || RECIPIENT_COLORS[idx % RECIPIENT_COLORS.length]
            }))
          }));
        }

        message.success('Document loaded successfully!');
      } else {
        throw new Error('Document data is missing.');
      }
    } catch (err) {
      console.error('Failed to fetch document:', err);
      setError('Unable to load the document. Please try again later.');
      message.error('Error loading the document.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchDocumentDetails();
  }, [id, fetchDocumentDetails]);

  // Handle field drop on PDF
  const handleDrop = ({ fieldType, recipientId, x, y, pageNumber }) => {
    if (!fieldType || !recipientId) {
      console.warn('Missing field type or recipient ID during drop.');
      return;
    }

    const fieldConfig = FIELD_TYPES.find(f => f.type === fieldType) || FIELD_TYPES[0];

    const newField = {
      id: uuidv4(),
      type: fieldType,
      position_x: x,
      position_y: y,
      width: fieldConfig.defaultWidth,
      height: fieldConfig.defaultHeight,
      recipient_id: recipientId,
      page: pageNumber,
      signatureData: null,
      custom_text: '',
      filled: false,
    };

    setFieldsByRecipient((prev) => {
      const updated = prev[recipientId]
        ? [...prev[recipientId], newField]
        : [newField];
      return { ...prev, [recipientId]: updated };
    });
  };

  // Handle field update via drag/resize
  const handleFieldUpdate = (updatedField) => {
    setFieldsByRecipient((prev) => {
      const recipientId = updatedField.recipient_id;
      if (!prev[recipientId]) return prev;
      return {
        ...prev,
        [recipientId]: prev[recipientId].map((f) =>
          f.id === updatedField.id ? updatedField : f
        ),
      };
    });
  };

  // Handle field removal
  const handleFieldRemove = async (fieldId, recipientId) => {
    try {
      // Try to delete from backend if it exists
      await actions.deleteDocumentField(id, fieldId);
      message.success('Field deleted successfully!');
    } catch (err) {
      // Field might not exist on backend yet, that's okay
      console.log('Field deletion:', err);
    }

    setFieldsByRecipient((prev) => {
      if (!prev[recipientId]) return prev;
      return {
        ...prev,
        [recipientId]: prev[recipientId].filter((f) => f.id !== fieldId),
      };
    });
  };

  // Navigation handlers
  const handleNext = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
    if (step < 4) setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep((prev) => prev - 1);
  };

  // Submit handler for each step
  const handleSubmit = async (currentStep, payload) => {
    try {
      let fieldsPayload = [];
      if (currentStep === 3) {
        fieldsPayload = getAllFields(fieldsByRecipient).map(
          ({ screenX, screenY, screenWidth, screenHeight, ...rest }) => ({
            ...rest,
            x: rest.position_x,
            y: rest.position_y,
          })
        );
      }

      const finalPayload = {
        ...payload,
        step: currentStep,
        fields: currentStep === 3 ? fieldsPayload : undefined,
      };

      const response = await actions.updateDocumentWorkflow(id, finalPayload);

      if (response) {
        message.success('Document saved successfully!');

        // Update form data with response
        if (response.document) {
          setFormData(response.document);

          // Update fields if returned
          if (response.document.fields && Array.isArray(response.document.fields)) {
            const normalized = response.document.fields.map((field) => ({
              ...field,
              recipient_id: field.recipient_id || field.recipientId,
            }));
            const grouped = normalized.reduce((acc, field) => {
              const rid = field.recipient_id;
              if (rid) {
                if (!acc[rid]) acc[rid] = [];
                acc[rid].push(field);
              }
              return acc;
            }, {});
            setFieldsByRecipient(grouped);
          }
        }

        // Move to next step if not on last step
        if (currentStep < 4) {
          handleNext({});
        } else {
          // On last step, navigate back or show success
          message.success('Document workflow completed! Sending for signatures...');
          navigate('/documents');
        }
      }
    } catch (err) {
      console.error('Failed to submit document:', err);
      message.error('Failed to save the document. Please try again.');
    }
  };

  // Get all fields for display
  const allFields = getAllFields(fieldsByRecipient);

  // Render current step content
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <StepGeneral
            formData={formData}
            onNext={handleNext}
            onSubmit={handleSubmit}
          />
        );
      case 2:
        return (
          <StepAddSigners
            formData={formData}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={handleSubmit}
            setFormData={setFormData}
            documentId={id}
          />
        );
      case 3:
        return (
          <StepAddFields
            formData={formData}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={handleSubmit}
            fieldsByRecipient={fieldsByRecipient}
            setFieldsByRecipient={setFieldsByRecipient}
            onActiveRecipientSelect={setActiveRecipient}
            activeRecipient={activeRecipient}
          />
        );
      case 4:
        return (
          <StepDistribution
            formData={formData}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={handleSubmit}
            documentId={id}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <Spin size="large" tip="Loading document..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', color: 'red' }}>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <Layout style={{ height: 'calc(100vh - 150px)', background: '#fff' }}>
      {/* Stepper Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
        <Stepper currentStep={step} />
      </div>

      {/* Main Content */}
      <Content style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
        {/* PDF Viewer - Left Panel (2/3 width) */}
        <div
          style={{
            width: '66.67%',
            borderRight: '1px solid #f0f0f0',
            overflow: 'auto',
            background: '#e8e8e8',
            position: 'relative',
          }}
        >
          {documentData ? (
            <DMSPDFViewer
              documentData={documentData}
              fields={allFields}
              handleDrop={step === 3 ? handleDrop : () => {}}
              onFieldUpdate={handleFieldUpdate}
              recipients={formData.recipients || []}
              activeRecipient={activeRecipient}
              onScaleChange={setScaleFactor}
              editable={step === 3 && Boolean(activeRecipient)}
              movable={step === 3 && Boolean(activeRecipient)}
              onFieldRemove={handleFieldRemove}
              showDelete={step === 3}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <p>No document to display.</p>
            </div>
          )}
        </div>

        {/* Step Content - Right Panel (1/3 width) */}
        <div
          style={{
            width: '33.33%',
            padding: '24px',
            overflow: 'auto',
            background: '#fff',
          }}
        >
          {renderStep()}
        </div>
      </Content>
    </Layout>
  );
};

export default DocumentWorkflow;
